import { useCommonFetch } from "../../../../../../hooks";
import {
  getJobApplicationList,
  updateJobApplication,
  updateJobApplicationBulk,
} from "../../../../../../services/workspace/recruitment.service";

import { useParams } from "react-router-dom";
import { useState } from "react";
import CommonTable from "../../../../../components/table/table";
import CommonModal from "../../../../../components/modals/commonModel";
import DefaultButton from "../../../../../components/buttons/defaultButton";
import { Icons, SidebarIcons } from "../../../../../../utils/icons";
import toast from "react-hot-toast";
import { tooltip } from "leaflet";
import BottomDrawer from "../../../../common/drawer/bottomDrawer";

const STATUS = {
  PENDING: 0,
  SHORTLIST: 1,
  SCHEDULE: 2,
  HOLD: 3,
  OFFERED: 4,
  HIRE: 5,
  REJECT: 6,
};

const useStatusModal = () => {
  const [isOpen, setOpen] = useState(false);
  return {
    isOpen,
    open: () => setOpen(true),
    close: () => setOpen(false),
  };
};

const customCase = {
  0: {
    label: "Pending",
    color: "text-blue-600 text-sm",
    bg: "bg-blue-600",
  },
  1: {
    label: "Shortlisted",
    color: "text-green-600 text-sm",
    bg: "bg-green-600",
  },
  2: {
    label: "Scheduled",
    color: "text-indigo-600 text-sm",
    bg: "bg-indigo-600",
  },
  3: {
    label: "On Hold",
    color: "text-yellow-600 text-sm",
    bg: "bg-yellow-600",
  },
  4: {
    label: "Offered",
    color: "text-purple-600 text-sm",
    bg: "bg-purple-600",
  },
  5: {
    label: "Hired",
    color: "text-emerald-600 text-sm",
    bg: "bg-emerald-600",
  },
  6: {
    label: "Rejected",
    color: "text-red-600 text-sm",
    bg: "bg-red-600",
  },
};

const OfferedList = () => {
  const { id } = useParams();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isOpenSheet, setIsOpenSheet] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // -------------------------------------------------
  // Status Modals
  // -------------------------------------------------
  const shortlistModal = useStatusModal();
  const scheduleModal = useStatusModal();
  const holdModal = useStatusModal();
  const offerModal = useStatusModal();
  const hireModal = useStatusModal();
  const rejectModal = useStatusModal();

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    handleSearchChange,
    handlePageChange,
    fetchData,
  } = useCommonFetch(getJobApplicationList, {
    dataPath: "jobApplication.data",
    totalPath: "jobApplication.meta.total",
    errorMessage: "Failed to fetch applications!",
    customParams: { job_opening_id: id, status: 4 },

    dataTransformer: (data) => {
      const transformed = data?.map((item) => ({
        id: item.uuid,
        name: { data: item.name, variant: "checkbox" },
        phone_number: { data: item.phone_number || "-", variant: "text" },
        email: { data: item.email || "-", variant: "text" },
        hear_from: { data: item.hear_from || "-", variant: "text" },
        experience: {
          data: item.experience ? `${item.experience} years` : "-",
          variant: "text",
        },
        address: { data: item.address || "-", variant: "text" },
        education: { data: item.education || "-", variant: "text" },
        current_role: { data: item.current_role || "-", variant: "text" },
        current_company: { data: item.current_company || "-", variant: "text" },
        reason_of_leaving: {
          data: item.reason_of_leaving || "-",
          variant: "text",
        },
        current_ctc: {
          data: item.current_ctc ? `₹${item.current_ctc}` : "-",
          variant: "text",
        },
        expected_ctc: {
          data: item.expected_ctc ? `₹${item.expected_ctc}` : "-",
          variant: "text",
        },
        key_skills: {
          data:
            item.key_skills && item.key_skills.length > 0
              ? item.key_skills.join(", ")
              : "-",
          variant: "text",
        },
        communication_skills: {
          data: item.communication_skills || "-",
          variant: "text",
        },
        availability_to_join: {
          data: item.availability_to_join
            ? `${item.availability_to_join} days`
            : "-",
          variant: "text",
        },
        notice_period: {
          data: item.notice_period ? `${item.notice_period} days` : "-",
          variant: "text",
        },
        created_at: { data: item.created_at, variant: "text" },
        status: {
          data: item?.status,
          className: "",
          variant: "status",
          customCases: customCase, // assuming you already have this defined somewhere
        },
      }));
      const action = {
        view: (data) => {
          setSelectedItem(data);
          setIsOpenSheet(true);
        },
      };

      return { data: transformed, action };
    },
  });

  const updateStatus = async (statusValue) => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one candidate.");
      return;
    }

    try {
      await updateJobApplicationBulk({
        job_application_uuids: selectedIds,
        status: statusValue,
        id: id,
      });

      toast.success("Status updated successfully.");
      setSelectedIds([]);
      fetchData({}, true);
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  const statusButtons = [
    {
      content: "Hired",
      status: STATUS.HIRE,
      icon: Icons.HIRE,
      modal: hireModal,
    },
    {
      content: "Rejected",
      status: STATUS.REJECT,
      icon: Icons.CLOSE,
      modal: rejectModal,
    },
  ];

  const TableHeaderData = [
    { label: "Candidate Name", key: "name" },
    { label: "Phone Number", key: "phone_number" },
    { label: "Email", key: "email" },
    { label: "Source", key: "hear_from" },
    { label: "Experience", key: "experience" },
    { label: "Location", key: "address" },
    { label: "Applied Date", key: "created_at" },
    { label: "Status", key: "status" },
  ];

  const actions = statusButtons.map((btn) => ({
    label: btn.label,
    icon: btn.icon,
    onClick: btn.modal.open,
    color: "text-primary-800",
    content: btn.content || "Action",
  }));


  const basicDetails = [
    // { label: "Applied For: ", value: selectedItem?.hear_from?.data },
    { label: "Last Role: ", value: selectedItem?.current_role?.data },
    {
      label: "Working/Worked at: ",
      value: selectedItem?.current_company?.data,
    },
    { label: "Location:", value: selectedItem?.address?.data },
    { label: "Email:", value: selectedItem?.email?.data },
    { label: "Phone: ", value: selectedItem?.phone_number?.data },
  ];

  const professionalDetails = [
    { label: "Total Experience: ", value: selectedItem?.experience?.data },
    {
      label: "Qualification: ",
      value: selectedItem?.education?.data,
    },
    { label: "Notice Period:", value: selectedItem?.notice_period?.data },
    { label: "Current Salary:", value: selectedItem?.current_ctc?.data },
    { label: "Expected Salary: ", value: selectedItem?.expected_ctc?.data },
    { label: "Key Skills: ", value: selectedItem?.key_skills?.data },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* TABLE */}
      <CommonTable
        searchEnable={true}
        searchPlaceholder="Search candidate..."
        onSearch={handleSearchChange}
        TableHeaderData={TableHeaderData}
        tabledata={tableData.data || []}
        currentPage={currentPage}
        totalData={totalData}
        action={tableData.action}
        setCurrentPage={handlePageChange}
        loading={loading}
        selectionKey="id"
        selectedRows={selectedIds}
        onSelectionChange={setSelectedIds}
        actions={actions}
      />

      {/* MODALS */}
      {statusButtons.map((btn) => (
        <CommonModal
          key={btn.label}
          show={btn.modal.isOpen}
          modalSize="sm"
          onClose={btn.modal.close}
        >
          <div className="flex flex-col items-center gap-4 p-4 text-center">
            <span className="text-4xl text-red-500">{Icons.WARNING}</span>

            <p className="font-semibold text-lg text-theme">
              Change status to {btn.content}?
            </p>

            <DefaultButton
              name="Confirm"
              onClick={() => {
                updateStatus(btn.status);
                btn.modal.close();
              }}
            />
          </div>
        </CommonModal>
      ))}

      <BottomDrawer
        classNames={"!w-[600px]"}
        title="Candidate Details"
        isOpen={isOpenSheet}
        setIsOpen={setIsOpenSheet}
      >
        {selectedItem && (
          <div className=" flex flex-col gap-4">
            {/* HEADER */}
            <div className="flex items-center gap-4 flex-row justify-between text-theme">
              <div>
                <h3 className="text-xl font-semibold">
                  {selectedItem.name.data}
                </h3>

                <p className="text-sm text-gray-600">
                  {/* Applied: {selectedItem.created_at || "-"} */}
                </p>
              </div>
              <span
                className={`text-xs px-3 py-1 rounded inline-block mt-2 ${
                  customCase[selectedItem.status.data]?.bg || "bg-gray-200"
                } text-white`}
              >
                {customCase[selectedItem.status.data]?.label}
              </span>
            </div>

            {/* BASIC DETAILS */}
            <div>
              <h2 className="text-theme font-semibold text-md border-b pb-2">
                Basic Details
              </h2>
              <div className="flex flex-col gap-2 p-2 pt-4">
                {basicDetails.map((item, idx) => (
                  <DetailCard key={idx} label={item.label} value={item.value} />
                ))}
              </div>
            </div>

            {/* PROFESSIONAL DETAILS */}
            <div>
              <h2 className="text-theme font-semibold text-md border-b pb-2">
                Professional Details
              </h2>
              <div className="flex flex-col gap-2 p-2">
                {professionalDetails.map((item, idx) => (
                  <DetailCard key={idx} label={item.label} value={item.value} />
                ))}
              </div>
            </div>
          </div>
        )}
      </BottomDrawer>
    </div>
  );
};
function DetailCard({ label, value }) {
  return (
    <div className="grid grid-cols-2 justify-between text-sm font-semibold">
      <span className="text-theme/60">{label}</span>
      <span className="font-medium text-theme">{value || "-"}</span>
    </div>
  );
}
export default OfferedList;
